// 回放（plan/vms-web-v0.md §5.6 PLAY-01 / 02 / 08；ADR-003 §2.5）：一條時間軸、每一刻一個來源。
//   - 攝影機 / station 通道：雲端優先，SD 補空檔；NVR / Kinio Station 通道：硬碟優先，NVR 連不上才看雲端（誰是 NVR 看能力，§5.19.1）
//   - MyCam 電腦端替它錄的攝影機：電腦端 → 雲端 → 手機本機 / SD（§5.19.3；覆蓋來自 façade recorder-coverage）
//   - 合併規則與缺口分類在 SDK（@kinio/web-sdk unified.ts），這頁只畫與播
//   - 雲端走 HLS（server 組好的 signed playlist）；本地走 lsrtc 回放隧道（F7）
//   - 播完一段自動接下一段；換來源或跳過缺口一律提示（不靜默跳過）
//   - 「全部來源」模式：各來源自己的覆蓋分行列出
//   - 時間軸互動照 SPEC #vms-web-timeline（PLAY-01 / W21）：PlaybackTimeline.tsx + timeline.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { BookmarkButton, BookmarkList } from './Bookmarks';
import Hls from 'hls.js';
import {
  KinioError,
  getHomeRecording,
  localSegmentSpans,
  localRecordingDays,
  localTimeline,
  mergeTimeline,
  nextSpan,
  nodeCoverageFromApi,
  nodeSourceIndexes,
  playlistHasSegments,
  spanAt,
  PlaybackTunnel,
  vms,
  type RecordingSource,
  type SourceIndex,
  type TimelineGap,
  type TimelineSpan,
  type UnifiedTimeline,
} from '@kinio/web-sdk';
import { useDevices } from '../../lib/useDevices';
import { backupSpans, cloudRecordingDays, cloudSpansGuarded, deviceEvents } from '../../lib/facade';
import { fragsSuspect, inSuspect, type ClipRange } from '../events/clipGuard';
import { DayPicker } from './DayPicker';
import { useLocalThumb, useThumb } from '../../lib/thumbs';
import { camerasOf, can, fullName, type VmsDevice } from '../../lib/devices';
import { localPlaylistUrl, makeTunnelLoader } from '../../lib/tunnelLoader';
import { DAY_SEC, assignmentsFromCoverage, snapToSpan, sourcePlan } from './view';
import { EVENT_GROUP_STYLE, EVENT_GROUPS, clampSpeed, collapsedAxis, eventGroup, linearAxis, skipCollapsed, speedOptions, toReal } from './timeline';
import { PlaybackTimeline, type TimelineEvent, type TimelineRowData } from './PlaybackTimeline';
import { addDays, formatDateTime, formatTime, tzLabel, zonedDayKey, zonedDayLength, zonedDayStart } from '../../lib/tz';
import { useHomeTz } from '../../lib/useTz';
import { errorKeys } from '../../lib/errorKeys';
import { exportHref, numParam, playbackHref } from '../../lib/links';
import { lensName, useLensDecl } from '../../lib/lens';
import { onPlaceCamera } from '../orgtree/placeCamera';
import { fetchEvent, idVariants } from '../../lib/eventLookup';
import { eventName, knownEventName } from '../../lib/eventName';
import { hasOrgPerm } from '../../lib/planeApi';
import { OutOfScopeHint } from '../../components/OutOfScopeHint';
import { useScope } from '../../lib/scope';
import { useApp } from '../../lib/AppContext';
import { SlowTierHintBar, useSlowTierHint } from './SlowTierHint';
import { PLAY_FALLBACK_MS, brokenSource, forgetBroken, lastOkSource, lastPlayedCamera, preferredSource, rememberBroken, rememberOk, srcParamOf, timeoutFallback } from './sourcePick';

/** 雲端索引多帶可疑片段的時段（片段長度防呆，第六輪 R6-P0-1、B6-01） */
type CloudIndex = SourceIndex & { suspect?: ClipRange[] };

type TunnelState = 'idle' | 'opening' | 'open' | 'failed' | 'closed';

/** 本地 RPC 的錯誤：設備不支援（沒有 SD / 舊韌體）→ 這個來源不存在；其他（逾時、離線）→ 來源未知。 */
function unsupported(e: unknown): boolean {
  const msg = e instanceof KinioError ? e.message : String(e);
  return /unknown (method|op)|not[_ ]?(supported|implemented|found)|no[_ ]?sd|method_not_found/i.test(msg);
}

/** 缺口的說明：原因 + 暫停原因 / 未知的來源（§5.19.3：「電腦目前離線」「電腦端暫停：過熱」）。 */
export function gapLabel(g: TimelineGap, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (g.reason === 'paused') return t('playback.gap_paused', { reason: t(`playback.pause_${g.cause}`, { defaultValue: g.cause ?? '' }) });
  if (g.reason === 'unknown' && g.cause && g.cause !== g.source) return t(`playback.unknown_${g.cause}`, { defaultValue: t('playback.gap_unknown') });
  return t(`playback.gap_${g.reason}`);
}

/** 雲端錄影查不到：沒權限 / 沒方案（403 / 404）→ 這個來源不存在。 */
function cloudAbsent(e: unknown): boolean {
  return e instanceof KinioError && (e.status === 403 || e.status === 404);
}

/** 播放錯誤：code = 錯誤碼（大寫 / 畫面自己的小寫碼），hls.js 的致命錯誤是 hls:<details>；source = 當時在播的來源。 */
export interface PlayErr {
  code: string;
  detail?: string;
  source?: RecordingSource;
  /** HTTP 狀態（402 沒有雲端錄影權益 / 席次、403 / 404 沒有回放權限） */
  status?: number;
}

const LOCAL_SOURCES: RecordingSource[] = ['nvr', 'sd', 'phone'];

type TFn = (k: string | string[], o?: Record<string, unknown>) => string;

/**
 * 播放錯誤 → 白話（B-06、UX-04）：不顯示 hls.js 的原字串（networkError: manifestLoadError…），原字串收進「詳細」。
 * 有翻譯的錯誤碼照翻；認不得的一律「這段錄影播不出來」＋詳細。
 */
export function playErrorText(t: TFn, err: PlayErr, camName: string, offline = false): { text: string; detail?: string } {
  const src = err.source ? t(`playback.src_${err.source}`) : '';
  const local = !!err.source && LOCAL_SOURCES.includes(err.source);
  const raw = [err.code, err.detail].filter(Boolean).join(' · ');
  // 回放錯誤對照表（SPEC #vms-web-459「回放錯誤與沒有錄影」）：白話 + 原字串只放「詳細」
  if (err.status === 402) return { text: t('playback.err_no_cloud'), detail: raw };
  if (err.status === 403 || err.status === 404) return { text: t('playback.err_forbidden'), detail: raw };
  if (local && offline && /^(hls:|TUNNEL_)/.test(err.code)) return { text: t('playback.err_local_offline', { name: camName }), detail: err.code.replace(/^hls:/, '') };
  if (err.code.startsWith('hls:')) {
    const d = err.code.slice(4);
    if (/load(error|timeout)|manifestParsingError|levelEmptyError/i.test(d))
      return { text: local ? t('playback.err_local_unreachable', { name: camName, src }) : t('playback.err_cloud_unreachable'), detail: d };
    if (/buffer|parsing|codec/i.test(d)) return { text: t('playback.err_decode'), detail: d };
    return { text: t('playback.err_generic'), detail: d };
  }
  if (/^TUNNEL_(FAILED|TIMEOUT|NOT_READY)$/.test(err.code) && local) {
    const own = t(errorKeys('playback', err.code), { defaultValue: '' });
    return { text: t('playback.err_local_unreachable', { name: camName, src }), detail: own || err.code };
  }
  if (err.source === 'cloud' && /^PLAYLIST_|PLAYBACK_FAILED/.test(err.code)) return { text: t('playback.err_cloud_unreachable'), detail: raw };
  const text = t(errorKeys('playback', err.code), { defaultValue: '' });
  return text ? { text, detail: err.detail } : { text: t('playback.err_generic'), detail: raw };
}

/** 網址的 src=cloud|local|backup → 時間軸的來源（local = 設備本機：NVR 硬碟 / SD 卡 / 手機）。 */
export function srcMatches(param: string | null, source: RecordingSource): boolean {
  if (!param) return true;
  if (param === 'local') return LOCAL_SOURCES.includes(source);
  return param === source;
}

/** 這一刻（或之後最近）有錄影的某個來源的段：給〔改看雲端錄影〕〔改看 <來源>〕用（不照「每一刻一個來源」的優先順序）。 */
export function coverageSpanAt(coverage: { source: RecordingSource; spans: TimelineSpan[] }[], want: (s: RecordingSource) => boolean, at: number): { span: TimelineSpan; start: number } | null {
  let best: { span: TimelineSpan; start: number } | null = null;
  for (const c of coverage) {
    if (!want(c.source)) continue;
    for (const sp of c.spans) {
      if (at >= sp.start && at < sp.end) return { span: sp, start: at };
      if (sp.start >= at && (!best || sp.start < best.start)) best = { span: sp, start: sp.start };
    }
  }
  return best;
}

/** 離這一刻最近的錄影段開頭（之後的優先，沒有就找之前最後一段）：〔跳到最近的錄影〕。 */
export function nearestRecording(spans: TimelineSpan[], at: number): number | null {
  const after = spans.find((sp) => sp.end > at);
  if (after) return Math.max(after.start, at);
  const before = spans.at(-1);
  return before ? before.start : null;
}

/** 逐格的一格（片段沒有帶 fps，照 Kinio 設備的預設 15 fps） */
const STEP_FPS = 15;


interface Playing {
  source: RecordingSource;
  from: number;
  to: number;
  /** 這次要播的連續同來源段到哪裡結束（清單一次只要一部分，播完從 to 接著要，見 playWindowSec） */
  until: number;
  span: TimelineSpan;
}

/**
 * 一份播放清單最多涵蓋多久（秒）。本地（NVR 硬碟 / SD 卡，經回放隧道）一次 10 分鐘：lsraw 卡一秒一段，一小時的清單約 480 KB，
 * 設備上行慢時光等清單就要十幾秒（SPEC #vms-web 本地回放清單、工單 tkt-01M4Z1KASWK3E0VCNXH044JV1V）；雲端 server playlist 一次一小時。
 * 播到清單結尾、那段還沒完 → onEnded 在同一條隧道接著要下一份（不重開隧道、不出提示）。
 */
export function playWindowSec(source: RecordingSource): number {
  return LOCAL_SOURCES.includes(source) ? 600 : 3600;
}

export function PlaybackPage() {
  const { t, i18n } = useTranslation();
  const { tree, loading } = useDevices();
  const { visible: visibleHomes, homes: allHomes, loading: scopeLoadingAll, orgs } = useScope();
  const location = useLocation();
  // 網址優先（/playback?cam=<home>/<aid>&t=<unix 秒>，P0-6）：重新整理、開新分頁、分享連結都還在；舊入口的 router state 相容
  const [sp, setSp] = useSearchParams();
  const navState: { focus?: string; at?: number } | null = sp.get('cam')
    ? { focus: sp.get('cam')!, at: numParam(sp, 't') }
    : (location.state as { focus?: string; at?: number } | null);
  // src=cloud|local|backup 指定先播哪個來源；from=<事件 id> 標出從哪個事件來（SPEC #vms-web-459 回放網址與上下文）
  const srcParam = sp.get('src');
  const [fromEvent, setFromEvent] = useState<string | null>(() => sp.get('from'));
  // 回放只列這台有 recording.read 的（「我的設備」的 actions，SPEC #device-permissions-ui）
  const cameras = useMemo(() => camerasOf(tree).filter((c) => can(c, 'recording.read')), [tree]);
  const [selKey, setSelKey] = useState<string | null>(() => navState?.focus ?? null);
  // 網址 / 入口指定了攝影機就只認那一台：找不到（不存在、已移除、沒有回放權限、不在範圍）不自動換成別台——
  // 換了會在別台開回放、留下錯的稽核（第四輪 B4-06）。沒指定才預設第一台在線的
  // 沒指定時（第六輪 B6-14、R6-P2-2）：這台電腦最近播過的那台 → 第一台在線的 → 第一台
  const lastPlayed = useMemo(() => lastPlayedCamera(cameras.map((c) => c.key)), [cameras]);
  const sel: VmsDevice | undefined = selKey
    ? cameras.find((c) => c.key === selKey)
    : (cameras.find((c) => c.key === lastPlayed) ?? cameras.find((c) => c.presence === 'online') ?? cameras[0]);
  const parent = sel?.parentAid ? tree.find((d) => d.aid === sel.parentAid && d.homeId === sel.homeId) : undefined;
  const cameraBody = !!sel?.isCamera && !sel.parentAid;
  const plan = useMemo(
    () => (sel ? sourcePlan(sel, parent) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sel?.key, parent?.aid, parent?.model, parent?.capabilities?.length, sel?.capabilities?.length, parent?.reportedClusters?.length, sel?.reportedClusters?.length, parent?.storageKinds?.join(), sel?.storageKinds?.join()],
  );
  const localOnline = (parent ?? sel)?.presence === 'online';
  // 多鏡頭（plan SPEC #multi-lens-playback）：回放哪顆鏡頭放網址 lens=（0 不帶）。時間軸畫的永遠是「拖下去會播的那顆」：
  // 選的鏡頭這天沒有雲端錄影（例 record_secondary 沒開）→ 改畫主鏡頭 + 說明「上鏡頭的錄影」。副鏡頭第一版只有雲端（lenses[i].sd 才問本地）。
  const decl = useLensDecl(sel);
  const lensWant = decl ? Math.min(Math.max(numParam(sp, 'lens') ?? 0, 0), decl.lenses.length - 1) : 0;

  // 時區（W16）：預設用場域時區（家戶 / 錨點場域），可切成瀏覽器時區；DST 那天時間軸 23 / 25 小時
  const { tz, siteTz, mode: tzMode, setMode: setTzMode } = useHomeTz(sel?.homeId);
  const firstPending = useRef(true);
  const [pendingAt, setPendingAt] = useState<number | null>(() => (typeof navState?.at === 'number' ? navState.at : null));
  // 最後選的時間點（書籤 PLAY-06 用：播放失敗也保留使用者選的位置）
  const [pickedAt, setPickedAt] = useState<number | null>(() => (typeof navState?.at === 'number' ? navState.at : null));
  const [day, setDay] = useState(() => zonedDayKey(typeof navState?.at === 'number' ? navState.at : Date.now() / 1000, tz));
  const today = zonedDayKey(Date.now() / 1000, tz);
  // 使用者還沒自己選日期時，時區載入 / 切換後「今天」跟著那個時區走
  const [dayPicked, setDayPicked] = useState(typeof navState?.at === 'number');
  useEffect(() => {
    if (!dayPicked) setDay(today);
  }, [today, dayPicked]);
  const ds = zonedDayStart(day, tz);
  const dayLen = zonedDayLength(day, tz);
  // 「檢視全部來源」記在這台電腦（第六輪 B6-09：換頁就不記得了）
  const [allSources, setAllSourcesState] = useState(() => {
    try {
      return localStorage.getItem(ALL_SOURCES_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setAllSources = (v: boolean) => {
    setAllSourcesState(v);
    try {
      localStorage.setItem(ALL_SOURCES_KEY, v ? '1' : '0');
    } catch {
      /* 存不了：這次有效就好 */
    }
  };
  // 「只看有事件的時段」：沒事件的時段折成細縫，播放時自動跳過（PLAY-01）
  const [onlyEvents, setOnlyEvents] = useState(false);
  // 中線最後在哪（真實秒）：切「只看有事件」時接著用
  const centerRef = useRef<number | null>(typeof navState?.at === 'number' ? navState.at - 5 : null);
  const [nowSec, setNowSec] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNowSec(Date.now() / 1000), 5000);
    return () => clearInterval(id);
  }, []);
  const [tunnelState, setTunnelState] = useState<TunnelState>('idle');
  const [playErr, setPlayErr] = useState<PlayErr | null>(null);
  // 縮圖（PLAY-01 / PLAY-05）：拖曳中的中線、滑過的游標（時間軸元件已做 250 ms / 300 ms 節流）→ 依那一刻的來源拿
  const [peekAt, setPeekAt] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [playing, setPlaying] = useState<Playing | null>(null);
  const playingRef = useRef<Playing | null>(null);
  playingRef.current = playing;
  /**
   * 單路的來源預設與回退（第三輪 R3-P0-3、B3-04；第五輪 R5-P0-1、B5-02 / B5-03；SPEC #vms-web-459「單路的來源預設與回退」）：
   * - 使用者自己選的來源放在網址 src=（〔改看 X〕會寫進去）：換攝影機、重新整理都帶著，不自動換（userSrc）
   * - 沒選：照 sourcePick.ts——這台最近成功播過的 → 從事件來先播雲端片段 → 合併時間軸的預設，避開 30 分鐘內讀不到的本地來源
   * - 本地來源讀不到（隧道 / HLS 致命錯誤）立刻、開始播 PLAY_FALLBACK_MS 還沒有畫面時，自動改播同一刻（或之後最近）有片段的其他來源
   * localBroken = 這台的本地來源這次讀不到：之後選段先避開它。
   */
  const userSrc = useRef(!!srcParam);
  userSrc.current = !!srcParam;
  // 隧道的鍵：主機＋通道（帶給平台記稽核的通道；換通道重開，稽核一個通道一列「看了 lstw-16ch / CH2 的本地錄影」，R11-P1-1）
  const hostKey = sel && plan ? `${sel.homeId}/${plan.localAid}${plan.auditCh !== undefined ? ':ch' + plan.auditCh : ''}` : '';
  const rememberedBroken = hostKey ? brokenSource(hostKey) : null;
  const [localBroken, setLocalBroken] = useState(false);
  const [fellBack, setFellBack] = useState<{ from: RecordingSource; at: number; kind: 'fail' | 'slow' | 'remembered' | 'suspect' } | null>(null);
  const [lastSource, setLastSource] = useState<RecordingSource | null>(null);
  /** 這次播放（使用者點的那一刻）已經自動試過、沒有畫面的來源：不來回換 */
  const triedRef = useRef<RecordingSource[]>([]);
  // 換攝影機：回退狀態重來（使用者選的來源在網址，留著）
  useEffect(() => {
    setLocalBroken(false);
    setFellBack(null);
    setLastSource(null);
    triedRef.current = [];
    pendingLocal.current = null;
  }, [sel?.key]);
  const [now, setNow] = useState<number | null>(null);
  const nowRef = useRef<number | null>(null);
  nowRef.current = now;
  const videoRef = useRef<HTMLVideoElement>(null);
  const { session } = useApp();
  // SD 回放跟不上：建議改錄影畫質（plan SPEC #device-app-sd-slow-tier）——只在獨立攝影機自己 SD 卡的本地回放判斷
  const slowDev = sel && plan && !sel.parentAid && plan.localAid === sel.aid && playing?.source === 'sd' ? sel : null;
  const slowHint = useSlowTierHint({ dev: slowDev, uid: session?.user?.user_id ?? '', playing: slowDev ? playing : null, videoRef });
  // 播放器的控制（第五輪 B5-04：不用瀏覽器原生的控制列，它的時間是媒體內部時間，跟時間軸對不上）
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(true);
  // 載入逾時（第四輪 B4-17、第五輪 R5-P0-1）：開始播 PLAY_FALLBACK_MS 還沒有畫面——沒指定來源就自動改用有片段的其他來源並說明；
  // 沒有其他來源（或使用者自己選了來源）才寫「載入很久」並給〔重試〕〔改看其他來源〕，不要一直轉圈
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!playing) return;
    const v = videoRef.current;
    const p = playing;
    const ok = () => {
      setSlow(false);
      if (sel && hostKey) rememberOk(sel.key, hostKey, p.source);
    };
    const id = window.setTimeout(() => {
      const cur = videoRef.current;
      if (cur && cur.readyState >= 2) return;
      if (!slowFallback(p)) setSlow(true);
    }, PLAY_FALLBACK_MS);
    v?.addEventListener('loadeddata', ok);
    v?.addEventListener('playing', ok);
    return () => {
      window.clearTimeout(id);
      v?.removeEventListener('loadeddata', ok);
      v?.removeEventListener('playing', ok);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);
  const tunnelRef = useRef<PlaybackTunnel | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const blobRef = useRef<string | null>(null);
  /** 播放中清單第一段的真實時間（秒；沒有 PROGRAM-DATE-TIME 時 null = 用 playing.from） */
  const baseRef = useRef<number | null>(null);

  // ---- 索引：本地（pb.timeline）+ 雲端（recordings）+ 家戶保留天數 ----
  const cloudOf = (lens: number) => async (): Promise<CloudIndex | null> => {
    const fromUs = ds * 1e6;
    const toUs = (ds + dayLen) * 1e6;
    try {
      // VMS façade（plan/vms-system-v0.md §4）：/vms/devices/{d}/recordings?source=cloud，≤ 24 小時一窗、cursor 翻頁
      // 長度不合理 / 結束在未來的片段截短（不畫未來、不蓋住之後的片段），可疑時段另外記（第六輪 B6-01）
      const g = await cloudSpansGuarded(sel!.apiEndpoint, sel!.homeId, plan!.cloudAid, fromUs / 1e6, toUs / 1e6, 40, Date.now() / 1000, lens);
      return { source: 'cloud', ok: true, spans: g.spans, suspect: g.suspect };
    } catch (e) {
      return cloudAbsent(e) ? null : { source: 'cloud', ok: false, spans: [] };
    }
  };
  // 選了副鏡頭：先問那顆這天有沒有雲端錄影（同一個 key，下面主查詢用到同一顆時共用結果）
  const lensCloud = useQuery({
    queryKey: ['recordings', sel?.homeId, plan?.cloudAid, day, tz, lensWant],
    enabled: !!sel && !!plan && lensWant > 0,
    retry: false,
    queryFn: cloudOf(lensWant),
  });
  const lensFallback = lensWant > 0 && lensCloud.data?.ok === true && lensCloud.data.spans.length === 0;
  /** 實際畫 / 播的鏡頭。 */
  const pbLens = lensFallback ? 0 : lensWant;
  const local = useQuery({
    queryKey: ['pb.timeline', sel?.homeId, plan?.localAid, plan?.localCh, day, localOnline, pbLens],
    staleTime: 30_000,
    enabled: !!sel && !!plan,
    retry: false,
    queryFn: async (): Promise<SourceIndex | null> => {
      if (pbLens && !decl?.lenses[pbLens]?.sd) return null; // 副鏡頭的本地錄影第一版沒有（lenses[i].sd 才問 pb.timeline）
      const cause = plan!.localKind === 'phone' ? { unknownCause: 'phone_offline' } : {};
      if (!localOnline) return { source: plan!.localKind, ok: false, spans: [], ...cause };
      try {
        // pb.timeline 的 date 是設備自己時區的日子；顯示時區可能不同 → 前後一天一起問，再裁到視窗
        const days = [addDays(day, -1), day, addDays(day, 1)];
        const rs = await Promise.all(
          days.map((d, i) => localTimeline(sel!.apiEndpoint, sel!.homeId, plan!.localAid, d, plan!.localCh).catch((e) => (i === 1 ? Promise.reject(e) : {}))),
        );
        const segs = rs.flatMap((r) => (r as Record<string, { start: number; end: number; types: number }[]>)[String(plan!.localCh)] ?? []);
        return { source: plan!.localKind, ok: true, spans: localSegmentSpans(segs) };
      } catch (e) {
        return unsupported(e) ? null : { source: plan!.localKind, ok: false, spans: [], ...cause };
      }
    },
  });
  const homeRec = useQuery({
    queryKey: ['home-recording', sel?.homeId],
    enabled: !!sel,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: () => getHomeRecording(sel!.apiEndpoint, sel!.homeId).catch(() => null),
  });
  const cloud = useQuery({
    queryKey: ['recordings', sel?.homeId, plan?.cloudAid, day, tz, pbLens],
    enabled: !!sel && !!plan && (lensWant === 0 || lensCloud.isFetched),
    retry: false,
    queryFn: cloudOf(pbLens),
  });
  const setLensParam = (l: number) => {
    const n = new URLSearchParams(sp);
    if (l) n.set('lens', String(l));
    else n.delete('lens');
    const at = now !== null ? now : pickedAt;
    if (at !== null) n.set('t', String(Math.round(at))); // 切鏡頭停在同一時間點（SPEC #multi-lens-playback）
    setSp(n, { replace: true });
    if (at !== null) {
      setPendingAt(at);
      setPickedAt(at);
    }
  };
  // MyCam 電腦端（§5.19.3）：façade recorder-coverage；往前多問 7 天（known_until 與「哪台負責」要看前面的上報）。
  // 沒有任何覆蓋 = 沒有電腦端替它錄 → 不列這個來源。指派歷史：recorder_assignments API 上線前由覆蓋推（view.ts）。
  const nodeCov = useQuery({
    queryKey: ['recorder-coverage', sel?.homeId, sel?.aid, day, tz],
    enabled: !!sel && !!plan && plan.order.includes('node'),
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<SourceIndex[]> => {
      try {
        const r = await vms.recorderCoverage(sel!.apiEndpoint, sel!.homeId, sel!.aid, {
          from: new Date((ds - 7 * DAY_SEC) * 1000).toISOString(),
          to: new Date((ds + dayLen) * 1000).toISOString(),
        });
        const rows = (r?.data ?? []).map(nodeCoverageFromApi);
        if (!rows.length) return [];
        return nodeSourceIndexes(assignmentsFromCoverage(rows), rows, { from: ds, to: ds + dayLen, now: Date.now() / 1000 });
      } catch {
        return [];
      }
    },
  });
  // 異地備份（BAK-04）：只有 NVR 的通道；沒有政策 / 沒有副本 = 不列這個來源
  const backup = useQuery({
    queryKey: ['backup-recordings', sel?.homeId, sel?.aid, day, tz],
    enabled: !!sel && !!plan && plan.order.includes('backup'),
    retry: false,
    queryFn: async (): Promise<SourceIndex | null> => {
      try {
        const spans = await backupSpans(sel!.apiEndpoint, sel!.homeId, sel!.aid, ds, ds + dayLen);
        return spans ? { source: 'backup', ok: true, spans } : null;
      } catch {
        return null;
      }
    },
  });
  // 事件標記：設備事件（通道的事件在 parent 上，payload.ch 分）
  const events = useQuery({
    queryKey: ['pb-events', sel?.homeId, sel?.aid, day, pbLens],
    enabled: !!sel,
    retry: false,
    queryFn: async () => {
      // façade 的事件（採集時蓋這個家戶章的；通道事件記在子設備，device 直接帶通道）；多鏡頭只標正在畫的那顆
      const list = await deviceEvents(sel!.apiEndpoint, sel!.homeId, sel!.aid, ds, ds + dayLen, 5, pbLens || undefined);
      // 標記依事件目錄 class 分組上色（跟 App 同一組）；沒對到群組的（上下線、OTA…）不上時間軸
      const out: { ts: number; group: NonNullable<ReturnType<typeof eventGroup>>; id: string; key: string; cls?: string; fields?: Record<string, unknown> }[] & { capped?: boolean } = list
        .map((ev) => ({ ev, ts: Date.parse(ev.occurred_at) / 1000, group: eventGroup({ class: ev.class ?? undefined, type: ev.key, payload: ev.fields }) }))
        .filter((x) => x.group !== null && x.ts >= ds && x.ts < ds + dayLen)
        .map((x) => ({ ts: x.ts, group: x.group!, id: x.ev.event_id, key: x.ev.key, cls: x.ev.class ?? undefined, fields: x.ev.fields }));
      if (list.capped) out.capped = true;
      return out;
    },
  });

  // 日曆（PLAY-01）：月曆顯示中的月份，有錄影的日子 = 雲端（façade recording-days）∪ 本地（設備 pb.days；離線就只有雲端）
  const [calMonth, setCalMonth] = useState(day.slice(0, 7));
  const calDays = useQuery({
    queryKey: ['pb-days', sel?.homeId, plan?.cloudAid, plan?.localAid, plan?.localCh, calMonth, tz, localOnline, lensWant],
    enabled: !!sel && !!plan,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const [c, l] = await Promise.all([
        cloudRecordingDays(sel!.apiEndpoint, sel!.homeId, plan!.cloudAid, calMonth, tz, lensWant).catch(() => [] as string[]),
        localOnline && plan!.localKind !== 'phone' && (!lensWant || decl?.lenses[lensWant]?.sd)
          ? localRecordingDays(sel!.apiEndpoint, sel!.homeId, plan!.localAid, calMonth, plan!.localCh).catch(() => [] as string[])
          : Promise.resolve([] as string[]),
      ]);
      return new Set([...c, ...l]);
    },
  });

  // 異地備份也要等（第六輪 B6-01：雲端片段可疑時要知道同一刻有沒有備份可改看）
  const ready = !local.isLoading && !cloud.isLoading && !nodeCov.isLoading && !backup.isLoading;
  const cloudSuspect = cloud.data?.suspect;
  /** 正在播的雲端清單裡有可疑片段（索引看起來正常、清單裡卻有一段很長 / 在未來）——跟索引的可疑時段一起當「可能不完整」 */
  const [hlsSuspect, setHlsSuspect] = useState(false);
  const tl: UnifiedTimeline | null = useMemo(() => {
    if (!plan || !ready) return null;
    const byKind = new Map<RecordingSource, SourceIndex>();
    // 本地索引一律標成「現在的計畫」的來源：機型資料（kinio_mycam）晚到時計畫會從 sd 改判 phone，查詢快取裡那份還標著 sd，
    // 照舊的標記放進去會被 order（['node','cloud','phone']）略過 → 時間軸沒有本地錄影、網址帶時間的自動播放改選雲端（MyCam MV16 實測約一半）
    if (local.data) byKind.set(plan.localKind, { ...local.data, source: plan.localKind });
    if (cloud.data) byKind.set('cloud', { ...cloud.data, retentionDays: homeRec.data?.retention_days || undefined });
    if (backup.data) byKind.set('backup', backup.data);
    const sources = plan.order.flatMap((k) => (k === 'node' ? (nodeCov.data ?? []) : [byKind.get(k)])).filter((s): s is SourceIndex => !!s);
    const nowSec = Date.now() / 1000;
    const offlineSince = sel?.presence === 'offline' && sel.lastSeen ? Date.parse(sel.lastSeen) / 1000 : NaN;
    return mergeTimeline(sources, {
      from: ds,
      to: ds + dayLen,
      now: nowSec,
      offline: Number.isFinite(offlineSince) ? [{ start: offlineSince, end: nowSec }] : [],
    });
  }, [plan, ready, local.data, cloud.data, backup.data, nodeCov.data, homeRec.data, ds, dayLen, sel?.presence, sel?.lastSeen]);

  // ---- 隧道：真的要播本地來源時才開（每台設備一條；換設備重開；離開頁面關掉） ----
  // 平台把開隧道記成一次「看了 X 的本地錄影」（recording.playback source=local）：只看雲端、只有本地索引時不開（第十輪 B10-02：
  // 單路播雲端、同一時刻看縮圖，稽核卻記「看了 lstw-16ch 的本地錄影 × 2 次」——原本只要本地索引讀得到就預先開隧道）
  const [tunnelWant, setTunnelWant] = useState('');
  /** 等隧道開好要播的本地片段 */
  const pendingLocal = useRef<{ span: TimelineSpan; start: number } | null>(null);
  const localReady = !!plan && local.data?.ok === true && localOnline;
  const needTunnel = localReady && !!hostKey && tunnelWant === hostKey;
  const linkKey = needTunnel && sel ? hostKey : '';
  useEffect(() => {
    if (!linkKey || !sel || !plan) {
      setTunnelState('idle');
      return;
    }
    // 播放起點（payload.from，epoch 秒）只給稽核寫「21:29 起的本地錄影」：開隧道時等著播的那一段從哪裡開始。
    // 同一台同一路的隧道開著時拖時間軸 / 換段不重開，平台照 SPEC 不記 seek；換攝影機 / 通道才是新隧道、帶新的起點（B12-04）
    const at = pendingLocal.current?.start;
    const tun = new PlaybackTunnel({
      apiEndpoint: sel.apiEndpoint,
      homeId: sel.homeId,
      deviceRef: plan.localAid,
      channel: plan.auditCh,
      ...(at !== undefined && Number.isFinite(at) ? { from: Math.floor(at) } : {}),
      onState: setTunnelState,
    });
    tunnelRef.current = tun;
    tun.open().catch((e) => {
      setTunnelState('failed');
      setLocalBroken(true);
      rememberBroken(`${sel.homeId}/${plan.localAid}`, plan.localKind);
      // 隧道開不起來只影響本地來源：正在播雲端時不蓋掉雲端的畫面與提示（第三輪 B3-04：一邊寫「從 09:15:55 開始播」一邊寫「讀不到 SD 卡」）
      const cur = playingRef.current;
      if (cur && !LOCAL_SOURCES.includes(cur.source)) return;
      const at = cur?.from ?? null;
      if (at !== null && fallbackFrom(plan.localKind, at)) return;
      if (cur) setPlayErr({ code: String(e?.code ?? 'TUNNEL_FAILED'), detail: e?.message ? String(e.message) : undefined, source: plan.localKind });
    });
    return () => {
      stopPlayer();
      tun.close();
      if (tunnelRef.current === tun) tunnelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKey]);

  useEffect(() => () => stopPlayer(), []);

  // 等隧道的本地片段：開好就播；開不起來照回退規則（改播其他來源或寫讀不到）
  useEffect(() => {
    const p = pendingLocal.current;
    if (!p) return;
    if (tunnelState === 'open' && tunnelRef.current) {
      pendingLocal.current = null;
      void startSpan(p.span, p.start);
    } else if (tunnelState === 'failed') {
      pendingLocal.current = null;
      if (!fallbackFrom(p.span.source, p.start)) setPlayErr({ code: 'TUNNEL_FAILED', source: p.span.source });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnelState]);

  function stopPlayer() {
    hlsRef.current?.destroy();
    hlsRef.current = null;
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    blobRef.current = null;
  }

  /** 連續、同來源的段接起來播：回 [這份清單的起, 迄, 整段連續的結尾]（清單長度照 playWindowSec：本地 10 分鐘、雲端 1 小時）。 */
  function playRange(span: TimelineSpan, start: number): [number, number, number] {
    const list = tl!.spans;
    let end = span.end;
    const at = list.indexOf(span);
    // 指定來源播的段（〔改看雲端錄影〕、src=）不在合併後的時間軸裡：只播那一段
    if (at >= 0) for (let i = at + 1; i < list.length && list[i].source === span.source && list[i].start <= end + 1; i++) end = list[i].end;
    return [start, Math.min(end, start + playWindowSec(span.source)), end];
  }

  // 雲端：片段縮圖（façade thumbnail）；NVR 硬碟 / SD：設備 pb.thumb（設備在線才問）；其他來源沒有縮圖
  const peekSrc = peekAt !== null ? tl?.spans.find((sp) => peekAt >= sp.start && peekAt < sp.end)?.source : undefined;
  const peekMs = peekAt === null ? null : Math.floor(peekAt) * 1000;
  const cloudThumb = useThumb(sel?.apiEndpoint, sel?.homeId ?? '', plan?.cloudAid ?? '', peekSrc === 'cloud' ? peekMs : null, !!sel && !!plan);
  const localThumbUrl = useLocalThumb(
    sel?.apiEndpoint,
    sel?.homeId ?? '',
    plan?.localAid ?? '',
    plan?.localCh ?? 0,
    peekSrc === 'nvr' || peekSrc === 'sd' ? peekMs : null,
    !!sel && !!plan && localOnline,
  );
  const peekUrl = peekSrc === 'cloud' ? cloudThumb : peekSrc === 'nvr' || peekSrc === 'sd' ? localThumbUrl : null;

  function gapText(gaps: TimelineGap[]): string {
    return gaps
      .map((g) => (g.reason === 'not_recorded' ? t('playback.gap_none_span', { from: fmt(g.start), to: fmt(g.end) }) : `${fmt(g.start)}–${fmt(g.end)} ${gapLabel(g, t)}`))
      .join(t('common.list_sep'));
  }

  async function startSpan(span: TimelineSpan, start: number) {
    if (!sel || !plan) return;
    const [from, to, until] = playRange(span, start);
    setPlayErr(null);
    setLastSource(span.source); // 來源膠囊寫這次要播的來源（播不出來也留著，B3-04）
    stopPlayer();
    const video = videoRef.current!;
    let hls: Hls;
    if (span.source === 'node') {
      // 電腦端的錄影經回放隧道即時問電腦端——mycamd 的定址契約還沒定（§5.19.3 MCN-04）
      setPlayErr({ code: 'NODE_PLAYBACK_UNAVAILABLE', source: 'node' });
      return;
    }
    if (span.source === 'backup') {
      // 備份副本：façade 的回放 session（source=backup）→ VMS proxy 從目的地取密文、驗完整性後解密（簽名 playlist，段是絕對網址）
      try {
        const ps = await vms.playbackSession(sel.apiEndpoint, sel.homeId, sel.aid, {
          source: 'backup',
          from: new Date(from * 1000).toISOString(),
          to: new Date(to * 1000).toISOString(),
        });
        if (ps.playback.kind !== 'hls' || !ps.playback.playlist_url) throw new Error('no playlist');
        hls = new Hls({ enableWorker: true, maxBufferLength: 30 });
        hls.loadSource(ps.playback.playlist_url);
      } catch (e) {
        setPlayErr({ code: String((e as KinioError).code ?? 'PLAYBACK_FAILED'), detail: (e as Error).message, source: 'backup', status: (e as KinioError).status });
        return;
      }
    } else if (span.source === 'cloud') {
      // façade 的回放 session（Idempotency-Key；每次取用重驗授權）→ 簽名 playlist
      let m3u8: string;
      let playlistURL: string;
      try {
        const ps = await vms.playbackSession(
          sel.apiEndpoint,
          sel.homeId,
          plan.cloudAid,
          {
            source: 'cloud',
            from: new Date(from * 1000).toISOString(),
            to: new Date(to * 1000).toISOString(),
          },
          undefined,
          pbLens,
        );
        if (ps.playback.kind !== 'hls') throw new Error('unexpected playback kind');
        playlistURL = ps.playback.playlist_url;
        const res = await fetch(playlistURL, { credentials: 'omit' });
        if (!res.ok) throw new KinioError(res.status, 'PLAYLIST_' + res.status, 'playlist');
        m3u8 = await res.text();
      } catch (e) {
        setPlayErr({ code: String((e as KinioError).code ?? 'PLAYBACK_FAILED'), detail: (e as Error).message, source: 'cloud', status: (e as KinioError).status });
        return;
      }
      if (!playlistHasSegments(m3u8)) {
        setPlayErr({ code: 'no_recording_after', source: 'cloud' });
        return;
      }
      // 段是相對路徑（clips/<id>），直接給 hls.js 簽名的 playlist 網址（不做 blob）
      hls = new Hls({ enableWorker: true, maxBufferLength: 30 });
      hls.loadSource(playlistURL);
    } else {
      if (!tunnelRef.current || tunnelState !== 'open') {
        // 隧道還沒開（只在真的要播本地時才開，B10-02）：記下這段、開隧道，開好再播
        if (tunnelState !== 'failed' && localReady && hostKey && !(tunnelWant === hostKey && tunnelState === 'closed')) {
          pendingLocal.current = { span, start };
          setTunnelWant(hostKey);
          return;
        }
        if (fallbackFrom(span.source, start)) return;
        setPlayErr({ code: tunnelState === 'failed' ? 'TUNNEL_FAILED' : 'TUNNEL_NOT_READY', source: span.source });
        setLastSource(span.source);
        return;
      }
      hls = new Hls({
        loader: makeTunnelLoader(() => tunnelRef.current),
        enableWorker: true,
        maxBufferLength: 20,
        fragLoadPolicy: {
          default: {
            maxTimeToFirstByteMs: 30_000,
            maxLoadTimeMs: 60_000,
            timeoutRetry: { maxNumRetry: 2, retryDelayMs: 500, maxRetryDelayMs: 2000 },
            errorRetry: { maxNumRetry: 2, retryDelayMs: 500, maxRetryDelayMs: 2000 },
          },
        },
      });
      hls.loadSource(localPlaylistUrl(plan.localCh, from, to));
    }
    hlsRef.current = hls;
    baseRef.current = null;
    // 清單第一段可能早於要播的時間（雲端片段從 22:26 開始、點的是 23:00）：照 EXT-X-PROGRAM-DATE-TIME 跳到點的那一刻，
    // 時鐘也照它算（第二輪 B2-11：點 23:00 不會從 22:26 播）
    setHlsSuspect(false);
    hls.once(Hls.Events.LEVEL_LOADED, (_e, data) => {
      if (hlsRef.current === hls && span.source === 'cloud' && fragsSuspect(data.details.fragments, Date.now() / 1000)) setHlsSuspect(true);
      const pdt = data.details.fragments[0]?.programDateTime;
      if (!pdt || hlsRef.current !== hls) return;
      const base = pdt / 1000;
      baseRef.current = base;
      const off = from - base;
      if (off > 1 && off < data.details.totalduration) video.currentTime = off;
    });
    setPlaying({ source: span.source, from, to, until, span });
    setLastSource(span.source);
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      // 致命錯誤：停掉播放器（不然影片一直轉圈），畫面改顯示白話錯誤與〔重試〕；本地來源讀不到先回退到有片段的其他來源
      if (hlsRef.current === hls) {
        stopPlayer();
        video.removeAttribute('src');
        video.load();
        setPlaying(null);
      }
      if (LOCAL_SOURCES.includes(span.source)) {
        setLocalBroken(true);
        if (hostKey) rememberBroken(hostKey, span.source);
        if (fallbackFrom(span.source, from)) return;
      }
      setPlayErr({ code: 'hls:' + data.details, source: span.source });
    });
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => void video.play().catch(() => {}));
  }

  // PLAY-04 變速與逐格：雲端 0.25–16×、逐格（暫停後前後一格，依片段 fps，沒有就當 15 fps）；本地來源隧道頻寬有限，最多 4×、不逐格
  const [rate, setRate] = useState(1);
  function stepFrame(dir: 1 | -1) {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.max(0, v.currentTime + dir / STEP_FPS);
  }
  useEffect(() => {
    const kind = playing?.source === 'cloud' ? 'cloud' : 'local';
    if (videoRef.current) videoRef.current.playbackRate = clampSpeed(rate, kind);
    if (playing && clampSpeed(rate, kind) !== rate) setRate(clampSpeed(rate, kind)); // 換來源保留倍速，超過上限降到上限
  }, [playing, rate]);

  /** 本地來源讀不到 → 改播同一刻（或之後最近）有片段的其他來源；使用者自己選了來源就不換。回 true = 換過去了。 */
  function fallbackFrom(failed: RecordingSource, at: number): boolean {
    if (userSrc.current || !tl || !sel) return false;
    const r = coverageSpanAt(tl.coverage, (x) => x !== failed && !(LOCAL_SOURCES.includes(x) && LOCAL_SOURCES.includes(failed)), at);
    if (!r) return false;
    setPlayErr(null);
    setFellBack({ from: failed, at, kind: 'fail' });
    setNotice(t('playback.fallback', { dev: camName, src: t(`playback.src_${failed}`), to: t(`playback.src_${r.span.source}`), at: fmt(r.start) }));
    void startSpan(r.span, r.start);
    return true;
  }

  /** 逾時沒有畫面（PLAY_FALLBACK_MS）：沒指定來源就改播有片段的其他來源；回 true = 換過去了。 */
  function slowFallback(p: Playing): boolean {
    if (userSrc.current || !tl || !sel) return false;
    const at = nowRef.current ?? p.from;
    if (LOCAL_SOURCES.includes(p.source)) {
      setLocalBroken(true);
      if (hostKey) rememberBroken(hostKey, p.source);
    }
    // 讀不到的本地來源（這次、或 30 分鐘內記下的）不當回退目標，免得在兩個都出不了畫面的來源之間來回換
    const broken = hostKey && plan && (localBroken || tunnelState === 'failed' || brokenSource(hostKey)) ? [plan.localKind] : [];
    // 可疑的雲端片段（片段長度防呆）也不當逾時回退的目標：換過去多半是綠畫面
    const suspectCloud: RecordingSource[] = inSuspect(cloudSuspect, at) ? ['cloud'] : [];
    const to = timeoutFallback(tl.coverage, p.source, [...triedRef.current, ...broken, ...suspectCloud], at);
    const r = to ? coverageSpanAt(tl.coverage, (x) => x === to, at) : null;
    if (!to || !r) return false;
    triedRef.current = [...triedRef.current, p.source];
    setPlayErr(null);
    setFellBack({ from: p.source, at, kind: 'slow' });
    setNotice(t('playback.slow_fallback', { src: t(`playback.src_${p.source}`), sec: PLAY_FALLBACK_MS / 1000, to: t(`playback.src_${to}`), at: fmt(r.start) }));
    void startSpan(r.span, r.start);
    return true;
  }

  /** 網址 src= 的顯示名（local = 這台的本地來源）。 */
  const srcParamLabel = (v: string) => t(`playback.src_${v === 'local' ? (plan?.localKind ?? 'sd') : v}`);

  type Choice = { span: TimelineSpan; start: number; notice: string | null; fellBack?: { from: RecordingSource; at: number; kind: 'fail' | 'remembered' | 'suspect' } } | null;

  /**
   * 雲端片段長度防呆（第六輪 R6-P0-1、B6-01；SPEC #vms-web-459「雲端片段長度防呆」）：選到的是可疑的雲端片段、使用者又沒指定雲端時，
   * 改播同一刻（或之後最近）有錄影的本地來源 / 異地備份，寫「雲端片段可能不完整，已改看 X〔改回雲端〕」；沒有其他來源才照播（畫面上另有說明）。
   */
  function avoidSuspect(c: Choice, at: number): Choice {
    if (!c || !tl || c.span.source !== 'cloud' || !inSuspect(cloudSuspect, c.start) || srcParam === 'cloud') return c;
    const broken = plan && (localBroken || tunnelState === 'failed' || rememberedBroken) ? plan.localKind : null;
    const r = coverageSpanAt(tl.coverage, (x) => x !== 'cloud' && x !== broken && x !== 'node', at);
    if (!r) return c;
    return {
      span: r.span,
      start: r.start,
      notice: t('playback.cloud_suspect_switched', { to: t(`playback.src_${r.span.source}`), at: fmt(r.start) }),
      fellBack: { from: 'cloud', at, kind: 'suspect' },
    };
  }

  /** 這一刻要播哪一段（來源的選法見上面 userSrc 的說明）；null = 這一刻之後都沒有。 */
  function choose(at: number): Choice {
    return avoidSuspect(chooseRaw(at), at);
  }

  function chooseRaw(at: number): Choice {
    if (!tl) return null;
    const moved = (start: number) => (start > at + 1 ? t('playback.seek_no_rec', { at: fmt(at), from: fmt(start) }) : null);
    // 使用者選了來源（網址 src=，換台也帶著；B5-03）：照它播；這台這段沒有那個來源才用預設，並說明
    let missing: string | null = null;
    if (srcParam) {
      const c = coverageSpanAt(tl.coverage, (x) => srcMatches(srcParam, x), at);
      if (c) return { span: c.span, start: c.start, notice: moved(c.start) };
      missing = t('playback.src_missing', { src: srcParamLabel(srcParam) });
    }
    const r = spanAt(tl, at);
    if (!r) return null;
    const join = (n: string | null) => [missing, n].filter(Boolean).join(' ') || null;
    if (!srcParam && sel && plan) {
      const pref = preferredSource(tl.coverage, at, {
        defaultSrc: r.span.source,
        lastOk: lastOkSource(sel.key),
        broken: localBroken || tunnelState === 'failed' ? plan.localKind : rememberedBroken,
        fromEvent: !!fromEvent,
      });
      const c = pref ? coverageSpanAt(tl.coverage, (x) => x === pref.src, at) : null;
      if (pref && c) {
        if (pref.why === 'avoid_broken') {
          const from = plan.localKind;
          // 這次就讀不到（隧道 / 索引）→「讀不到 X，改播雲端」〔重試讀 X〕；之前記下的 →「X 剛才讀不到，先播雲端」〔改回 X〕
          const now = localBroken || tunnelState === 'failed';
          return {
            span: c.span,
            start: c.start,
            notice: now
              ? t('playback.fallback', { dev: camName, src: t(`playback.src_${from}`), to: t(`playback.src_${pref.src}`), at: fmt(c.start) })
              : t('playback.avoid_broken', { dev: camName, src: t(`playback.src_${from}`), to: t(`playback.src_${pref.src}`), at: fmt(c.start) }),
            fellBack: { from, at, kind: now ? 'fail' : 'remembered' },
          };
        }
        return { span: c.span, start: c.start, notice: moved(c.start) };
      }
    }
    // 點在沒有錄影的地方：「23:00 沒有錄影，從 23:04 開始播。」（SPEC 點時間軸的落點；第二輪 B2-11）
    // 其他跳過缺口：「22:27:10–22:27:49 沒有錄影，已從 22:27:49 開始播」（P0-6 ③）
    return {
      span: r.span,
      start: r.start,
      notice: join(r.start > at + 1 ? t('playback.seek_no_rec', { at: fmt(at), from: fmt(r.start) }) : r.skipped.length ? t('playback.skipped_from', { gaps: gapText(r.skipped), at: fmt(r.start) }) : null),
    };
  }

  function play(at: number) {
    setPickedAt(at);
    setFellBack(null);
    triedRef.current = [];
    if (!tl) return;
    const c = choose(at);
    if (!c) {
      setPlayErr({ code: 'no_recording_after' });
      return;
    }
    // 本地來源這次讀不到、又沒指定來源：選到本地的段時，改播同一刻（或之後）其他來源的片段，提示講同一個來源（B3-04）
    if (!srcParam && LOCAL_SOURCES.includes(c.span.source) && (localBroken || tunnelState === 'failed') && fallbackFrom(c.span.source, at)) return;
    setNotice(c.notice);
    if (c.fellBack) setFellBack(c.fellBack);
    void startSpan(c.span, c.start);
  }

  /** 使用者選了來源：寫進網址 src=（換攝影機、重新整理都帶著；B5-03）。 */
  function pinSource(src: RecordingSource) {
    const v = srcParamOf(src);
    if (!v) return;
    setSp(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set('src', v);
        return n;
      },
      { replace: true },
    );
  }

  /** 指定來源播（〔改看雲端錄影〕〔改看 <來源>〕、網址 src=）：那一刻那個來源沒有就從它之後最近的一段開始。 */
  function playFrom(want: (s: RecordingSource) => boolean, at: number, chosen = true): boolean {
    if (!tl) return false;
    if (chosen) userSrc.current = true;
    setFellBack(null);
    const r = coverageSpanAt(tl.coverage, want, at);
    if (!r) return false;
    if (chosen) {
      pinSource(r.span.source);
      if (hostKey && LOCAL_SOURCES.includes(r.span.source)) forgetBroken(hostKey);
    }
    setPickedAt(at);
    setNotice(r.start > at + 1 ? t('playback.skipped_from', { gaps: t('playback.gap_none_span', { from: fmt(at), to: fmt(r.start) }), at: fmt(r.start) }) : null);
    void startSpan(r.span, r.start);
    return true;
  }

  /** 拿掉使用者選的來源，回到自動選（標題列來源膠囊旁的〔自動〕）。 */
  function autoSource() {
    const n = new URLSearchParams(sp);
    n.delete('src');
    setSp(n, { replace: true });
    userSrc.current = false;
    const at = nowRef.current ?? pickedAt;
    if (at !== null) setPendingAt(at + 5);
  }

  // 一段播完 → 下一段；換來源或跳過缺口都要提示
  function onEnded() {
    if (!tl || !playing) return;
    // 清單只要了一部分（本地 10 分鐘 / 雲端 1 小時）、這段連續錄影還沒完：從清單結尾接著要下一份，同來源、同隧道、不出提示
    // （原本 nextSpan 從「清單結尾所在那一段」的下一段找，會跳過這段剩下的部分）
    if (playing.until > playing.to + 1) {
      void startSpan(playing.span, playing.to);
      return;
    }
    const n = nextSpan(tl, playing.to);
    if (!n) {
      setNotice(t('playback.end_of_day'));
      return;
    }
    const parts: string[] = [];
    if (n.skipped.length) parts.push(t('playback.skipped_from', { gaps: gapText(n.skipped), at: fmt(Math.max(n.span.start, playing.to)) }));
    if (n.sourceChanged) parts.push(t('playback.source_changed', { from: t(`playback.src_${playing.source}`), to: t(`playback.src_${n.span.source}`) }));
    setNotice(parts.length ? parts.join(' ') : null);
    void startSpan(n.span, Math.max(n.span.start, playing.to));
  }

  useEffect(() => {
    if (pendingAt === null || !tl) return;
    if (selKey && sel?.key !== selKey) return; // 網址指定的攝影機不在範圍裡：不要在別台播那個時間
    // SD 事件的本機入口精確跳到事件時間；其他入口保留原有的前 5 秒。
    const sdEvent = firstPending.current && cameraBody && srcParam === 'local' && sp.get('rec_target') === 'sd';
    if (sdEvent && local.isLoading) return;
    const at = sdEvent ? pendingAt : pendingAt - 5;
    const c = choose(at);
    if (c && LOCAL_SOURCES.includes(c.span.source) && needTunnel && tunnelState !== 'open' && tunnelState !== 'failed') return; // 等隧道（開不起來就照回退規則）
    firstPending.current = false;
    if (sdEvent) centerRef.current = at;
    setPendingAt(null);
    play(at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAt, tl, tunnelState]);

  // 當天結束（隔天 00:00）顯示成 24:00:00，整天的缺口才不會寫成「00:00:00 – 00:00:00」
  const fmt = (sec: number, withSeconds = true) => (sec >= ds + dayLen - 0.5 && sec < ds + dayLen + 0.5 ? (withSeconds ? '24:00:00' : '24:00') : formatTime(sec, tz, i18n.language, withSeconds));

  const evTimes = useMemo(() => (events.data ?? []).map((e) => e.ts), [events.data]);
  const axis = useMemo(
    () => (onlyEvents ? collapsedAxis(ds, dayLen, evTimes, nowSec) : linearAxis(ds, dayLen)),
    // 「只看有事件」的軸只在事件 / 日子變了才重算（nowSec 每 5 秒走，只影響最後一段的尾巴）
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlyEvents, ds, dayLen, evTimes, onlyEvents ? Math.floor(nowSec / 60) : 0],
  );
  const tlEvents: TimelineEvent[] = useMemo(
    () => (events.data ?? []).map((e) => ({ ts: e.ts, group: e.group, id: e.id, title: knownEventName(t, e.key, e.fields, sel?.aid, e.cls) || t(`playback.group_${e.group}`) })),
    [events.data, t],
  );
  const rows: TimelineRowData[] = useMemo(
    () => [
      { key: 'unified', label: t('playback.unified'), spans: tl?.spans ?? [], gaps: tl?.gaps ?? [] },
      ...(allSources
        ? (tl?.coverage ?? []).map((c, i, all) => ({
            key: c.source + (c.ref ?? ''),
            // 同一種來源有好幾個（例：兩個異地備份目的地）時用序號分開，不顯示目的地 ID（P0-7）
            label: `${t(`playback.src_${c.source}`)}${all.filter((x) => x.source === c.source).length > 1 ? ' ' + (all.slice(0, i + 1).filter((x) => x.source === c.source).length) : ''}${c.ok ? '' : ' · ' + t('playback.gap_unknown')}`,
            spans: c.spans,
            gaps: [],
          }))
        : []),
    ],
    [tl, allSources, t],
  );
  // 只看有事件：整條（所有事件時段）攤開，中線放中間；其他：上次的中線 → 今天是現在、過去的日子是中午
  const initialCenter = onlyEvents
    ? toReal(axis, axis.length / 2)
    : centerRef.current !== null && centerRef.current >= ds && centerRef.current < ds + dayLen
      ? centerRef.current
      : day === today
        ? nowSec
        : ds + dayLen / 2;
  const initialSpan = onlyEvents ? Math.max(60, axis.length) : centerRef.current !== null && navState?.at !== undefined ? 3600 : dayLen;

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (!playing) {
      if (centerRef.current !== null) play(centerRef.current);
      return;
    }
    if (v.paused) void v.play().catch(() => {});
    else v.pause();
  }

  // 「只看有事件的時段」：播到折掉的時段就跳到下一段
  const skipping = useRef(false);
  function onClock(at: number) {
    setNow(at);
    if (!onlyEvents || !axis.collapsed || skipping.current) return;
    const next = skipCollapsed(axis, at);
    if (next === null) {
      videoRef.current?.pause();
      setNotice(t('playback.end_of_events'));
    } else if (next > at + 0.5) {
      skipping.current = true;
      play(next);
      setTimeout(() => (skipping.current = false), 1000);
    }
  }

  const sourcesShown: RecordingSource[] = tl ? tl.coverage.map((c) => c.source) : [];
  const camName = sel ? fullName(sel, tree) : '';
  const playErrView = playErr ? playErrorText(t as TFn, playErr, camName, !localOnline) : null;
  const retryAt = pickedAt ?? centerRef.current;
  // 匯出這段：目前時間點前 5 分鐘起、10 分鐘（網址帶參數，P0-6）；正在播的來源一起帶（匯出對話框預選有錄影的來源）
  const exportFromHere = (cam: string, at: number | null) =>
    exportHref(cam, at !== null ? at - 300 : null, 600, playing ? (playing.source === 'cloud' ? 'cloud' : playing.source === 'backup' ? 'backup' : 'local') : undefined);
  // 0 段錄影（P0-6 ④、PLAY-08）：說原因（依這台的實際狀況列），有別天的錄影就給捷徑
  const noRecording = ready && !!tl && tl.spans.length === 0;
  const lastRecDay = noRecording ? [...(calDays.data ?? [])].filter((d) => d < day).sort().at(-1) : undefined;
  const unreachable = tl?.coverage.some((c) => !c.ok) ?? false;
  const noRecReasons = noRecording && tl ? noRecordingReasons(t as TFn, { plan, cloudAbsent: cloud.data === null, offline: tl.gaps.some((g) => g.reason === 'offline'), camName }) : [];
  const dayEvents = events.data?.length ?? 0;
  // 網址 / 入口指定的攝影機不在目前範圍（或沒有回放權限）：提示，不默默換成別台
  const camMissing = !loading && !scopeLoadingAll && !!selKey && !cameras.some((c) => c.key === selKey);
  // 家戶在別的範圍（看得到、只是不在目前範圍）→ 提示可切範圍；整個找不到 → 「找不到這台攝影機」，讓使用者自己選
  const focusMissing = camMissing && !visibleHomes.some((h) => h.home_id === selKey!.split('/')[0]) && allHomes.some((h) => h.home_id === selKey!.split('/')[0]);
  const camNotFound = camMissing && !focusMissing;
  // 網址指定的攝影機不在範圍：清掉上一台留下的播放器、來源膠囊與提示（第六輪 B6-08）
  useEffect(() => {
    if (!camMissing) return;
    stopPlayer();
    const v = videoRef.current;
    if (v) {
      v.removeAttribute('src');
      v.load();
    }
    setPlaying(null);
    setLastSource(null);
    setNow(null);
    setNotice(null);
    setPlayErr(null);
    setFellBack(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camMissing]);
  // 整天沒有錄影時不寫「0 段錄影、1 個缺口」（第六輪 B6-14）
  const status = !ready ? t('common.loading') : tl ? (tl.spans.length ? t('playback.summary', { spans: tl.spans.length, gaps: tl.gaps.length }) : t('playback.day_no_rec')) : '';
  const selectCamera = (key: string) => {
    // 換攝影機保留時間點（第四輪 B4-05）：在同一刻開始播；那一刻沒有錄影就照「沒有錄影」的規則說原因
    const at = now !== null ? now + 5 : (pickedAt ?? (centerRef.current !== null ? centerRef.current + 5 : null));
    setSelKey(key);
    // 使用者選的來源（src=）跟著帶到新的那台（第五輪 B5-03）；新的那台沒有這個來源時 choose() 用預設並說明
    const q: Record<string, string> = { cam: key }; // 換攝影機：鏡頭回主鏡頭（lens 不帶）
    if (at !== null) q.t = String(Math.round(at));
    if (srcParam) q.src = srcParam;
    setSp(q, { replace: true });
    if (at !== null) {
      setPendingAt(at);
      setPickedAt(at);
    }
    setFromEvent(null);
    stopPlayer();
    setPlaying(null);
    setNow(null);
    setPlayErr(null);
    setNotice(null);
  };
  // 攝影機用左側組織樹選（點即換，跟即時影像同一個樹；SPEC #vms-web-459 回放頁版面）
  const camsRef = useRef(cameras);
  camsRef.current = cameras;
  const selectRef = useRef(selectCamera);
  selectRef.current = selectCamera;
  useEffect(() => onPlaceCamera((key) => camsRef.current.some((c) => c.key === key) && selectRef.current(key)), []);

  // 正在播的雲端片段可疑（片段長度防呆，第六輪 B6-01）：畫面上說明，同一刻有其他來源就給〔改看 X〕
  const playingSuspect = !!playing && playing.source === 'cloud' && (hlsSuspect || inSuspect(cloudSuspect, now ?? playing.from));
  const suspectAlt = playingSuspect && tl && playing ? coverageSpanAt(tl.coverage, (x) => x !== 'cloud' && x !== 'node', now ?? playing.from)?.span.source : undefined;
  // 錯誤的下一步（SPEC「回放錯誤與沒有錄影」表）
  const cloudCovered = !!tl?.coverage.some((c) => c.source === 'cloud' && c.spans.length > 0);
  const errLocal = !!playErr?.source && LOCAL_SOURCES.includes(playErr.source);
  const slowOther = slow && playing && tl ? tl.coverage.find((c) => c.source !== playing.source && c.spans.some((x) => x.end > (now ?? playing.from)))?.source : undefined;
  const otherSrc = playErr && tl && retryAt !== null ? tl.coverage.find((c) => c.source !== playErr.source && c.spans.some((x) => x.end > retryAt))?.source : undefined;
  const canBuy = orgs.some((o) => hasOrgPerm(o, 'org.billing')) || visibleHomes.some((h) => h.org_kind === 'personal' && (h.role === 'owner' || h.role === 'admin'));
  const nearest = playErr?.code === 'no_recording_after' && tl && retryAt !== null ? nearestRecording(tl.spans, retryAt) : null;
  // 這一刻（或之後）沒有可播的段：逐來源說原因（第二輪 B2-09），第一句放在影片中央
  const localStore = plan?.localKind === 'nvr' ? true : plan?.localKind === 'sd' ? !!(parent ?? sel)?.storageKinds?.includes('sd') : false;
  const why =
    playErr?.code === 'no_recording_after' && plan && retryAt !== null
      ? noRecordingWhy(t as TFn, {
          camName,
          localKind: plan.localKind === 'sd' || plan.localKind === 'nvr' ? plan.localKind : null,
          localHasStorage: localStore,
          local: local.data,
          localOnline,
          tunnelFailed: tunnelState === 'failed',
          cloud: cloud.data,
          retentionDays: homeRec.data?.retention_days || undefined,
          at: retryAt,
          now: nowSec,
        })
      : [];
  const retryLocal = why.some((w) => w.key === 'local_unreachable');
  const noRecAfter = playErr?.code === 'no_recording_after';

  // 從事件來的膠囊「從事件：移動偵測 · 3KPT · 22:27:48 ✕」
  const fromEv = fromEvent ? (events.data ?? []).find((e) => idVariants(fromEvent).has(e.id)) : undefined;
  const fromEvQ = useQuery({
    queryKey: ['pb-from-event', sel?.homeId, fromEvent],
    enabled: !!sel && !!fromEvent && events.isSuccess && !fromEv,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: () => fetchEvent(sel!.apiEndpoint, sel!.homeId, fromEvent!).catch(() => null),
  });
  const fromEvName = fromEv
    ? eventName(t, fromEv.key, fromEv.cls, fromEv.fields) // 跟事件詳細同一個顯示名（第二輪 R2-P0-3 ⑥：「聽到狗叫」不變成「辨識到聲音」）
    : fromEvQ.data
      ? eventName(t, fromEvQ.data.key, fromEvQ.data.class, fromEvQ.data.fields)
      : t('playback.from_event_generic');
  const fromEvAt = fromEv?.ts ?? (fromEvQ.data ? Date.parse(fromEvQ.data.occurred_at) / 1000 : (navState?.at ?? null));

  // 時間軸右上〔⚙〕開著與否（「只看有事件的時段」切換時時間軸會換一個元件，開關狀態要留著）
  const [settingsOpen, setSettingsOpen] = useState(false);
  // ⚙ 浮層按 Esc 或點外面就關（第六輪 B6-09：原本要再點一次 ⚙，浮層一直蓋著時間軸右半）
  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSettingsOpen(false);
    const onDown = (e: MouseEvent) => !(e.target instanceof Element && e.target.closest('.pb-settings')) && setSettingsOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [settingsOpen]);
  // 時間軸第一次使用時的提示（記在瀏覽器）
  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return localStorage.getItem(HINT_KEY) === '1';
    } catch {
      return true;
    }
  });
  const dismissHint = () => {
    setHintSeen(true);
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      /* 存不了：這次關掉就好 */
    }
  };

  return (
    <section className="pb-page">
      <div className="wall-area">
        {focusMissing && <OutOfScopeHint homeId={selKey!.split('/')[0]} kind="device" />}
        {camNotFound && (
          <p className="card scope-hint" role="status" data-testid="pb-cam-not-found">
            {t('playback.cam_not_found')}
          </p>
        )}
        {/* 標題列「回放 · 3KPT · SD 卡」：攝影機選單在標題旁（左側組織樹點了也會換），不再佔一整列 */}
        <div className="toolbar pb-toolbar">
          <h1 className="page-title" style={{ margin: 0 }}>
            {t('nav.playback')}
          </h1>
          <label className="pb-camera-picker">
            <span className="sr-only">{t('live.cameras')}</span>
            <select className="input" value={sel?.key ?? ''} disabled={loading || !cameras.length} onChange={(e) => selectCamera(e.target.value)} title={camName}>
              {!cameras.length && <option value="">{t(loading ? 'common.loading' : 'devices.empty')}</option>}
              {cameras.length > 0 && !sel && (
                <option value="" disabled>
                  {t('playback.pick_camera')}
                </option>
              )}
              {cameras.map((camera) => (
                <option key={camera.key} value={camera.key}>
                  {fullName(camera, tree)}
                </option>
              ))}
            </select>
          </label>
          {decl && (
            <div className="pb-lens tile-lens" role="radiogroup" aria-label={t('lens.group_playback')} data-testid="pb-lens">
              {decl.lenses.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  role="radio"
                  aria-checked={l.id === lensWant}
                  tabIndex={l.id === lensWant ? 0 : -1}
                  className={`tile-lens-seg${l.id === lensWant ? ' on' : ''}`}
                  data-lens={l.id}
                  onClick={() => l.id !== lensWant && setLensParam(l.id)}
                  onKeyDown={(e) => {
                    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
                    if (!d) return;
                    e.preventDefault();
                    setLensParam((l.id + d + decl.lenses.length) % decl.lenses.length);
                  }}
                >
                  {lensName(t, decl, l.id)}
                </button>
              ))}
            </div>
          )}
          {/* 來源膠囊一直在（第三輪 B3-04）：播放出錯時寫最後一次試的來源 */}
          {(playing?.source ?? lastSource) && (
            <span className={`badge src-badge src-${playing?.source ?? lastSource}`} data-testid="pb-source" title={srcParam ? t('playback.src_pinned') : t('playback.src_auto_hint')}>
              {cameraBody && (playing?.source ?? lastSource) === 'sd' ? t('playback.recording_sd') : t(`playback.src_${playing?.source ?? lastSource}`)}
            </span>
          )}
          {/* 使用者自己選了來源（網址 src=）：換台也帶著；〔自動選來源〕拿掉（第五輪 B5-03） */}
          {srcParam && !camMissing && (
            <button className="link-btn small" onClick={autoSource} data-testid="pb-src-auto">
              {t('playback.src_auto')}
            </button>
          )}
          {needTunnel && (tunnelState === 'failed' || tunnelState === 'opening') && (
            <span className={`badge tunnel-${tunnelState}`} data-testid="pb-tunnel-state">
              {t(`playback.tunnel_${tunnelState}`)}
            </span>
          )}
          <span className="spacer" />
          <PlaybackModes mode="single" cameras={sel ? [sel.key] : []} at={now ?? pendingAt ?? pickedAt ?? undefined} />
        </div>
        {fromEvent && (
          <p className="pb-from-event" data-testid="pb-from-event">
            <span className="chip on">
              {t('playback.from_event', { name: fromEvName, cam: camName, at: fromEvAt !== null ? formatTime(fromEvAt, tz, i18n.language, true) : '' })}
              <button
                className="icon-btn"
                aria-label={t('playback.from_event_dismiss')}
                title={t('playback.from_event_dismiss')}
                onClick={() => {
                  setFromEvent(null);
                  const n = new URLSearchParams(sp);
                  n.delete('from');
                  setSp(n, { replace: true });
                }}
              >
                ✕
              </button>
            </span>
          </p>
        )}
        <div className="toolbar pb-actions">
          <DayPicker
            value={day}
            max={today}
            month={calMonth}
            onMonth={setCalMonth}
            marked={calDays.data}
            onPick={(d) => {
              setDayPicked(true);
              centerRef.current = null;
              setDay(d);
              stopPlayer();
              setPlaying(null);
              setNow(null);
            }}
          />
          {sel && (
            <Link className="btn btn-sm" to={exportFromHere(sel.key, now ?? pendingAt ?? pickedAt ?? centerRef.current)} data-testid="pb-export">
              {t('exports.export_this')}
            </Link>
          )}
          {sel && <BookmarkButton dev={sel} at={now ?? pendingAt ?? pickedAt} tz={tz} />}
        </div>
        <div className="pb-video">
          {/* 不用原生控制列（第五輪 B5-04：它顯示媒體內部時間「10:40:06 / 23:52:50」，跟時間軸對不上）；播放 / 暫停 / 聲音 / 全螢幕在下面的控制列 */}
          <video
            ref={videoRef}
            muted={muted}
            playsInline
            onClick={togglePlay}
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
            onEnded={onEnded}
            onTimeUpdate={(e) => (playing ? onClock((baseRef.current ?? playing.from) + (e.currentTarget.currentTime || 0)) : setNow(null))}
          />
          {!playing && !playErrView && <div className="tile-overlay">{t('playback.pick_time')}</div>}
          {!playing && playErrView && (
            <div className={`tile-overlay${noRecAfter ? ' pb-norec-overlay' : ''}`} data-testid="pb-error-overlay">
              {/* 沒有錄影的原因只寫在影片上一次（第五輪 B5-05：不再在下面用紅字重寫一次）；第一句最主要 */}
              {why.length > 1 ? (
                <ul className="plain-list" data-testid="pb-why">
                  {why.map((w) => (
                    <li key={w.key} data-why={w.key}>
                      {w.text}
                    </li>
                  ))}
                </ul>
              ) : (
                <span data-testid={why.length ? 'pb-why' : undefined}>{why[0]?.text ?? playErrView.text}</span>
              )}
            </div>
          )}
          {playing && now !== null && <div className="pb-clock">{formatDateTime(now, tz, i18n.language, true)}</div>}
        </div>
        {playing && (
          <div className="pb-speed toolbar" data-testid="pb-speed">
            <button className="btn btn-sm" onClick={togglePlay} aria-pressed={!paused} data-testid="pb-playpause">
              {paused ? t('playback.play') : t('playback.pause')}
            </button>
            <button
              className="btn btn-sm"
              onClick={() => {
                setMuted((m) => !m);
              }}
              aria-pressed={!muted}
              data-testid="pb-mute"
            >
              {muted ? t('playback.sound_on') : t('playback.sound_off')}
            </button>
            <button className="btn btn-sm" onClick={() => void videoRef.current?.parentElement?.requestFullscreen?.().catch(() => {})} data-testid="pb-fullscreen">
              {t('playback.fullscreen')}
            </button>
            <label className="muted small">
              {t('playback.speed')}{' '}
              <select
                className="input"
                style={{ width: 'auto' }}
                value={rate}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setRate(v);
                  if (videoRef.current) videoRef.current.playbackRate = v;
                }}
              >
                {speedOptions(playing.source === 'cloud' ? 'cloud' : 'local').map((x) => (
                  <option key={x} value={x}>
                    {x}×
                  </option>
                ))}
              </select>
            </label>
            {playing.source === 'cloud' && (
              <>
                <button className="btn btn-sm" aria-label={t('playback.frame_back')} onClick={() => stepFrame(-1)}>
                  ⏮
                </button>
                <button className="btn btn-sm" aria-label={t('playback.frame_fwd')} onClick={() => stepFrame(1)}>
                  ⏭
                </button>
              </>
            )}
          </div>
        )}
        {/* 提示 / 播放錯誤固定佔一格：非同步冒出來時不把時間軸往下推（使用者正要拖時間軸，版面一跳就按到別處） */}
        <div className="pb-msgs">
          {lensFallback && decl && (
            <p className="pb-notice" role="status" data-testid="pb-lens-fallback">
              <strong>{t('lens.timeline_main', { name: lensName(t, decl, 0) })}</strong> · {t('lens.no_rec', { name: lensName(t, decl, lensWant) })}
            </p>
          )}
          {playingSuspect && playing && (
            <p className="pb-notice" role="status" data-testid="pb-cloud-suspect">
              {t('playback.cloud_suspect')}
              {suspectAlt && (
                <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => playFrom((x) => x === suspectAlt, now ?? playing.from)} data-testid="pb-suspect-other">
                  {t('playback.try_source', { src: t(`playback.src_${suspectAlt}`) })}
                </button>
              )}
            </p>
          )}
          {slow && playing && !playErrView && (
            <p className="pb-notice" role="status" data-testid="pb-slow">
              {t('playback.slow', { src: t(`playback.src_${playing.source}`) })}{' '}
              <button className="btn btn-sm" onClick={() => play(now ?? playing.from)} data-testid="pb-slow-retry">
                {t('common.retry')}
              </button>{' '}
              {slowOther && (
                <button className="btn btn-sm" onClick={() => playFrom((x) => x === slowOther, now ?? playing.from)} data-testid="pb-slow-other">
                  {t('playback.try_source', { src: t(`playback.src_${slowOther}`) })}
                </button>
              )}
            </p>
          )}
          <SlowTierHintBar hint={slowHint} />
          {notice && (
            <p className="pb-notice" role="status" data-testid="pb-notice">
              {notice}
              {fellBack && (
                <button
                  className="btn btn-sm"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setLocalBroken(false);
                    setPlayErr(null);
                    if (hostKey) forgetBroken(hostKey);
                    if (LOCAL_SOURCES.includes(fellBack.from)) void local.refetch();
                    playFrom((x) => x === fellBack.from, fellBack.at);
                  }}
                  data-testid="pb-retry-fallback"
                >
                  {fellBack.kind === 'fail' ? t('playback.retry_local', { src: t(`playback.src_${fellBack.from}`) }) : t('playback.back_to', { src: t(`playback.src_${fellBack.from}`) })}
                </button>
              )}
            </p>
          )}
          {playErrView && (
            // 沒有錄影不是錯誤：用一般提示色、只放下一步按鈕（原因已寫在影片上，B5-05）；真的播放錯誤才用錯誤色並寫一次
            <div className={noRecAfter ? 'pb-notice pb-norec' : 'error'} data-testid="pb-error">
              {noRecAfter ? null : playErrView.text}{' '}
              {retryLocal && retryAt !== null && (
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    setPlayErr(null);
                    void local.refetch();
                    setPendingAt(retryAt + 5);
                  }}
                  data-testid="pb-retry-local"
                >
                  {t('playback.retry_local', { src: t(`playback.src_${plan!.localKind}`) })}
                </button>
              )}
              {retryAt !== null && playErr?.code !== 'no_recording_after' && !playErr?.status && (
                <button className="btn btn-sm" onClick={() => play(retryAt)} data-testid="pb-retry">
                  {t('common.retry')}
                </button>
              )}
              {retryAt !== null && errLocal && cloudCovered && (
                <button className="btn btn-sm" onClick={() => playFrom((x) => x === 'cloud', retryAt)} data-testid="pb-try-cloud">
                  {t('playback.try_cloud')}
                </button>
              )}
              {nearest !== null && (
                <button className="btn btn-sm" onClick={() => play(nearest)} data-testid="pb-nearest">
                  {t('playback.go_nearest')}
                </button>
              )}
              {retryAt !== null && otherSrc && !(errLocal && otherSrc === 'cloud') && (
                <button className="btn btn-sm" onClick={() => playFrom((x) => x === otherSrc, retryAt)} data-testid="pb-try-other">
                  {t('playback.try_source', { src: t(`playback.src_${otherSrc}`) })}
                </button>
              )}
              {playErr?.status === 402 && !otherSrc && canBuy && (
                <Link className="btn btn-sm" to="/billing#seats" data-testid="pb-buy-seats">
                  {t('playback.buy_seats')}
                </Link>
              )}
              {playErrView.detail && (
                <details className="small muted">
                  <summary>{t('playback.err_detail')}</summary>
                  <span className="mono">{playErrView.detail}</span>
                </details>
              )}
            </div>
          )}
        </div>
        {noRecording && (
          <div className="card small" data-testid="pb-no-recording">
            <p style={{ margin: 0 }}>
              {sourcesShown.length === 0 ? t('playback.no_sources') : unreachable ? t('playback.no_recording_unreachable') : t('playback.no_recording_day')}
            </p>
            {sourcesShown.length > 0 && noRecReasons.length > 0 && (
              <ul className="plain-list muted" data-testid="pb-no-rec-reasons">
                {noRecReasons.map((r) => (
                  <li key={r}>· {r}</li>
                ))}
              </ul>
            )}
            {dayEvents > 0 && (
              <p className="muted" style={{ margin: '4px 0 0' }} data-testid="pb-no-rec-events">
                {t('playback.no_rec_events', { count: dayEvents })}
              </p>
            )}
            {lastRecDay && (
              <button
                className="btn btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setDayPicked(true);
                  centerRef.current = null;
                  setDay(lastRecDay);
                }}
                data-testid="pb-last-rec-day"
              >
                {t('playback.go_last_day', { day: lastRecDay })}
              </button>
            )}
          </div>
        )}
        {sel && <BookmarkList dev={sel} from={ds} to={ds + dayLen} tz={tz} onPick={(at) => play(at)} />}
        {events.isError && (
          <p role="alert" data-testid="pb-events-error">
            {t('common.error')} <button onClick={() => void events.refetch()}>{t('common.retry')}</button>
          </p>
        )}
        {!hintSeen && (
          <p className="notice small pb-hint" role="note" data-testid="pb-drag-hint">
            {t('playback.drag_hint')}{' '}
            <button className="link-btn" onClick={dismissHint}>
              {t('playback.hint_ok')}
            </button>
          </p>
        )}
        {/* 時區、圖例、「只看有事件的時段」「檢視全部來源」收進時間軸右上〔⚙〕（P1-4） */}
        {(() => {
          const settings = (
            <details className="pb-settings" data-testid="pb-settings" open={settingsOpen} onToggle={(e) => setSettingsOpen(e.currentTarget.open)}>
              <summary aria-label={t('playback.settings')} title={t('playback.settings')}>
                ⚙
              </summary>
              <div className="pb-settings-pop card">
                <p className="muted small" style={{ margin: 0 }}>
                  {status}
                </p>
                <label className="check">
                  <input type="checkbox" checked={onlyEvents} onChange={(e) => setOnlyEvents(e.target.checked)} data-testid="pb-only-events" />
                  {t('playback.only_events')}
                </label>
                <label className="check">
                  <input type="checkbox" checked={allSources} onChange={(e) => setAllSources(e.target.checked)} data-testid="pb-all-sources" />
                  {t('playback.all_sources')}
                </label>
                <label className="tz-pick muted">
                  {t('playback.tz')}
                  <select className="input" value={tzMode} onChange={(e) => void setTzMode(e.target.value as 'site' | 'browser')}>
                    <option value="site" disabled={!siteTz}>
                      {t('playback.tz_site')}
                      {siteTz ? ` · ${siteTz}` : ''}
                    </option>
                    <option value="browser">{t('playback.tz_browser')}</option>
                  </select>
                  <span className="badge">{tzLabel(tz, ds + dayLen / 2)}</span>
                </label>
                <div className="pb-timeline-head">
                  {(['node', 'cloud', 'nvr', 'sd', 'phone', 'backup'] as RecordingSource[])
                    .filter((x) => sourcesShown.includes(x))
                    .map((x) => (
                      <span key={x} className={`legend src-${x}`}>
                        {t(`playback.src_${x}`)}
                      </span>
                    ))}
                  {EVENT_GROUPS.filter((g) => tlEvents.some((e) => e.group === g)).map((g) => (
                    <span key={g} className="legend ev-group" data-legend={g} style={{ ['--ev' as string]: EVENT_GROUP_STYLE[g].color }}>
                      <span aria-hidden>{EVENT_GROUP_STYLE[g].icon}</span> {t(`playback.group_${g}`)}
                    </span>
                  ))}
                  {(['offline', 'retention', 'not_recorded', 'unknown'] as const)
                    .filter((r) => tl?.gaps.some((g) => g.reason === r))
                    .map((r) => (
                      <span key={r} className={`legend gap-${r}`}>
                        {t(`playback.gap_${r}`)}
                      </span>
                    ))}
                </div>
              </div>
            </details>
          );
          const toolbar = (
            <>
              {dayLen !== DAY_SEC && <span className="badge">{t('playback.dst_day', { h: dayLen / 3600 })}</span>}
              <span className="spacer" />
              {settings}
            </>
          );
          return onlyEvents && axis.length === 0 && !events.isError ? (
            <>
              <div className="toolbar">
                <span className="spacer" />
                {settings}
              </div>
              <p className="muted" data-testid="pb-no-events">
                {t('playback.no_events_day')}
              </p>
            </>
          ) : (
            <PlaybackTimeline
              key={`${sel?.key ?? ''}|${day}|${tz}|${onlyEvents ? 'ev' : 'all'}`}
              axis={axis}
              now={nowSec}
              playhead={playing ? now : null}
              initialCenter={initialCenter}
              initialSpan={initialSpan}
              rows={rows}
              events={tlEvents}
              eventsCapped={!!events.data?.capped}
              dayLabels={[day, addDays(day, 1)]}
              fmt={fmt}
              gapText={(g) => `${fmt(g.start)} – ${fmt(g.end)} · ${gapLabel(g, t)}`}
              srcText={(x) => t(`playback.src_${x}`)}
              t={t}
              onSeek={(at) => {
                centerRef.current = at;
                if (!hintSeen) dismissHint();
                if (sel) {
                  const n = new URLSearchParams(sp);
                  n.set('cam', sel.key);
                  n.set('t', String(Math.floor(at)));
                  setSp(n, { replace: true });
                }
                play(at);
              }}
              onTogglePlay={togglePlay}
              onCenter={(at) => {
                centerRef.current = at;
              }}
              snap={(at, tol) => snapToSpan(tl?.spans ?? [], at, tol)}
              onPeek={setPeekAt}
              peek={peekAt !== null && peekUrl ? { at: peekAt, url: peekUrl } : null}
              toolbar={toolbar}
            />
          );
        })()}
        {tl && tl.gaps.length > 0 && tl.spans.length > 0 && (
          <details className="pb-gaps">
            <summary>{t('playback.gaps_title', { count: tl.gaps.length })}</summary>
            <ul>
              {tl.gaps.map((g, i) => (
                <li key={i}>
                  <span className={`legend gap-${g.reason}`} />
                  {fmt(g.start)} – {fmt(g.end)} · {gapLabel(g, t)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

const HINT_KEY = 'kinio.vms.pbDragHint.v1';
const ALL_SOURCES_KEY = 'kinio.vms.pbAllSources.v1';

/**
 * 整天 0 段錄影的可能原因（SPEC #vms-web-459「整天 0 段錄影」）：依這台的實際狀況列，不列不相關的。
 */
export function noRecordingReasons(
  t: TFn,
  o: { plan: { localKind: RecordingSource; order: string[] } | null; cloudAbsent: boolean; offline: boolean; camName: string },
): string[] {
  const out: string[] = [];
  const local = o.plan && (o.plan.localKind === 'sd' || o.plan.localKind === 'nvr') ? o.plan.localKind : null;
  if (local) out.push(t('playback.why_overwritten', { src: t(`playback.src_${local}`) }));
  if (o.cloudAbsent) out.push(t('playback.why_no_cloud'));
  if (local === 'nvr') out.push(t('playback.why_schedule'));
  if (o.offline) out.push(t('playback.why_offline', { name: o.camName }));
  return out;
}

/** 沒有錄影的原因（逐來源一句；第一句最主要）——SPEC #vms-web-459「沒有錄影一律說原因」，第二輪 B2-09。 */
export type NoRecWhy = { key: 'local_unreachable' | 'local_offline' | 'local_unsupported' | 'cloud_no_clip' | 'cloud_retention' | 'cloud_absent' | 'cloud_unreachable' | 'local_empty'; text: string };
export function noRecordingWhy(
  t: TFn,
  o: {
    camName: string;
    /** 本地來源（sd / nvr；phone、沒有本地來源 = null） */
    localKind: 'sd' | 'nvr' | null;
    /** 設備回報有這種儲存（SD 卡攝影機 storage_kinds 含 sd；NVR 一律 true） */
    localHasStorage: boolean;
    /** 本地索引：undefined = 還沒問、null = 設備說不支援、ok=false = 沒回應 */
    local: SourceIndex | null | undefined;
    localOnline: boolean;
    tunnelFailed: boolean;
    /** 雲端索引：null = 沒有雲端錄影（403 / 404）、ok=false = 讀不到 */
    cloud: SourceIndex | null | undefined;
    retentionDays?: number;
    at: number;
    now: number;
  },
): NoRecWhy[] {
  const out: NoRecWhy[] = [];
  const src = o.localKind ? t(`playback.src_${o.localKind}`) : '';
  let localEmpty = false;
  if (o.localKind) {
    if (!o.localOnline) out.push({ key: 'local_offline', text: t('playback.why_local_offline', { name: o.camName, src }) });
    else if (o.local?.ok === false || o.tunnelFailed || (o.local === null && o.localHasStorage))
      out.push({ key: 'local_unreachable', text: t('playback.why_local_unreachable', { name: o.camName, src }) });
    else if (o.local?.ok) localEmpty = !o.local.spans.some((x) => x.end > o.at);
  }
  if (o.cloud === null) out.push({ key: 'cloud_absent', text: t('playback.err_no_cloud') });
  else if (o.cloud?.ok === false) out.push({ key: 'cloud_unreachable', text: t('playback.err_cloud_unreachable') });
  else if (o.cloud?.ok && !o.cloud.spans.some((x) => x.end > o.at)) {
    if (o.retentionDays && o.at < o.now - o.retentionDays * 86400) out.push({ key: 'cloud_retention', text: t('playback.why_cloud_retention', { n: o.retentionDays }) });
    else out.push({ key: 'cloud_no_clip', text: t('playback.why_cloud_no_clip', { name: o.camName }) });
  }
  if (localEmpty) out.push({ key: 'local_empty', text: t('playback.why_local_empty', { src }) });
  return out;
}

/** 分段之間帶的網址參數：cam（多路同步可以多台，逗號分隔）、t（unix 秒）。 */
export function modeHref(mode: 'sync' | 'moment', cameras: string[], at?: number | null): string {
  const q = new URLSearchParams();
  if (cameras.length) q.set('cam', (mode === 'moment' ? cameras.slice(0, 1) : cameras).join(','));
  if (typeof at === 'number' && Number.isFinite(at)) q.set('t', String(Math.floor(at)));
  return `/playback/${mode}${q.size ? '?' + q : ''}`;
}

/** 回放頁的分段「單路｜多路同步｜同一時刻」（P1-4：取代原本並排的同款按鈕）；切換時帶 cam / t（第二輪 B2-10）。 */
export function PlaybackModes({ mode, cameras = [], at }: { mode: 'single' | 'sync' | 'moment'; cameras?: string[]; at?: number | null }) {
  const { t } = useTranslation();
  return (
    <nav className="seg pb-modes" aria-label={t('playback.modes')}>
      {/* 從多路同步 / 同一時刻切回單路不帶 src（只帶攝影機與時間，照沒選來源的預設選）：src= 只由使用者自己選來源時寫入，
          不然沒有雲端片段時會出現「改用其他來源」與〔改回自動選來源〕，像是使用者做錯事（SPEC「單路的來源預設與回退」⑤；第十三輪 B13-01） */}
      <Link
        to={cameras[0] ? playbackHref(cameras[0], at) : '/playback'}
        aria-current={mode === 'single' ? 'page' : undefined}
        data-testid="pb-single"
      >
        {t('playback.mode_single')}
      </Link>
      <Link to={modeHref('sync', cameras, at)} state={{ cameras, at }} aria-current={mode === 'sync' ? 'page' : undefined} data-testid="pb-sync">
        {t('sync.open')}
      </Link>
      <Link to={modeHref('moment', cameras, at)} aria-current={mode === 'moment' ? 'page' : undefined} data-testid="pb-moment">
        {t('moment.title')}
      </Link>
    </nav>
  );
}
